import React, { useEffect, useState } from 'react';
import { Modal, Table, InputNumber, message, Empty, Typography, Tag, Segmented, Button, Row, Col, Space, Avatar } from 'antd';
import {
  ThunderboltOutlined, TableOutlined, UserOutlined, ArrowLeftOutlined,
  PlusOutlined, MinusOutlined,
} from '@ant-design/icons';
import api from '../../services/api';

const { Text } = Typography;

const FIELDS = [
  { key: 'goals',        emoji: '⚽', label: 'Gols',        short: 'G',  color: '#52c41a' },
  { key: 'assists',      emoji: '🅰️', label: 'Assist',      short: 'A',  color: '#1890ff' },
  { key: 'yellow_cards', emoji: '🟨', label: 'Amarelos',    short: 'CA', color: '#faad14' },
  { key: 'blue_cards',   emoji: '🟦', label: 'Azuis',       short: 'CAz', color: '#1677ff' },
  { key: 'red_cards',    emoji: '🟥', label: 'Vermelhos',   short: 'CV', color: '#f5222d' },
  { key: 'fouls',        emoji: '🚨', label: 'Faltas',      short: 'F',  color: '#722ed1' },
  { key: 'own_goals',    emoji: '🥅', label: 'Gols contra', short: 'GC', color: '#8c8c8c' },
];

const StatsInputModal = ({ open, gameId, gameLabel, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState('quick'); // 'quick' | 'batch'
  const [selectedPlayer, setSelectedPlayer] = useState(null); // player_id selecionado no modo Rápido

  useEffect(() => {
    if (!open || !gameId) return;
    setLoading(true);
    setSelectedPlayer(null);
    api.get(`/organizer-games/${gameId}/stats`)
      .then(r => setRows(r.data?.players || []))
      .catch(err => message.error(err?.response?.data?.error || 'Erro ao carregar jogadores'))
      .finally(() => setLoading(false));
  }, [open, gameId]);

  const updateCell = (playerId, field, value) => {
    const v = Math.max(0, Math.min(99, parseInt(value, 10) || 0));
    setRows(prev => prev.map(r => r.player_id === playerId ? { ...r, [field]: v } : r));
  };

  const bumpCell = (playerId, field, delta) => {
    setRows(prev => prev.map(r => {
      if (r.player_id !== playerId) return r;
      const cur = Number(r[field] || 0);
      return { ...r, [field]: Math.max(0, Math.min(99, cur + delta)) };
    }));
  };

  const persist = async () => {
    await api.put(`/organizer-games/${gameId}/stats`, { stats: rows });
  };

  const saveAndClose = async () => {
    setSaving(true);
    try {
      await persist();
      message.success('Estatísticas salvas');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Erro ao salvar');
    }
    setSaving(false);
  };

  const saveAndBackToList = async () => {
    setSaving(true);
    try {
      await persist();
      message.success('Salvo, escolha outro jogador');
      onSaved && onSaved();
      setSelectedPlayer(null); // volta pra lista sem fechar o modal
    } catch (err) {
      message.error(err?.response?.data?.error || 'Erro ao salvar');
    }
    setSaving(false);
  };

  const totalStatsFor = (r) => FIELDS.reduce((s, f) => s + Number(r[f.key] || 0), 0);

  const columns = [
    {
      title: 'Jogador', dataIndex: 'name', fixed: 'left', width: 160,
      render: (v, row) => (
        <span>
          <Text strong>{v}</Text>
          {row.is_guest && <Tag color="orange" style={{ marginLeft: 6, fontSize: 10 }}>Convidado</Tag>}
        </span>
      ),
    },
    ...FIELDS.map(f => ({
      title: <span>{f.emoji} {f.label}</span>,
      dataIndex: f.key,
      align: 'center',
      width: 90,
      render: (val, row) => (
        <InputNumber
          min={0} max={99}
          value={val}
          onChange={(v) => updateCell(row.player_id, f.key, v)}
          size="small"
          style={{ width: 60 }}
          disabled={row.is_guest}
        />
      ),
    })),
  ];

  const quickPanel = () => {
    // Lista de jogadores
    if (!selectedPlayer) {
      return (
        <>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            Toca no jogador pra lançar as estatísticas. Você pode voltar e trocar quantas vezes quiser antes de salvar.
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map(r => {
              const total = totalStatsFor(r);
              const clickable = !r.is_guest;
              return (
                <div
                  key={r.gp_id || r.player_id}
                  onClick={() => clickable && setSelectedPlayer(r.player_id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: 12, borderRadius: 12,
                    background: r.is_guest ? '#fafafa' : (total > 0 ? '#f6ffed' : '#fafafa'),
                    border: `1px solid ${r.is_guest ? '#f0f0f0' : (total > 0 ? '#b7eb8f' : '#e8e8e8')}`,
                    cursor: clickable ? 'pointer' : 'not-allowed',
                    opacity: r.is_guest ? 0.65 : 1,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (clickable) e.currentTarget.style.background = '#e6f7ff'; }}
                  onMouseLeave={e => e.currentTarget.style.background = r.is_guest ? '#fafafa' : (total > 0 ? '#f6ffed' : '#fafafa')}
                >
                  <Avatar icon={<UserOutlined />} style={{ background: r.is_guest ? '#bfbfbf' : '#25D366', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Text strong style={{ fontSize: 14 }}>{r.name}</Text>
                      {r.is_guest && <Tag color="orange" style={{ margin: 0, fontSize: 10 }}>Convidado</Tag>}
                    </div>
                    <Space size={4} wrap style={{ marginTop: 2 }}>
                      {r.is_guest ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>Cadastre no time pra lançar stats</Text>
                      ) : total === 0 ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>toque pra lançar</Text>
                      ) : (
                        FIELDS.filter(f => r[f.key] > 0).map(f => (
                          <Tag key={f.key} color={f.color} style={{ margin: 0, fontSize: 11 }}>
                            {f.emoji} {r[f.key]}
                          </Tag>
                        ))
                      )}
                    </Space>
                  </div>
                  {clickable && <div style={{ color: '#8c8c8c', fontSize: 18 }}>›</div>}
                </div>
              );
            })}
          </div>
        </>
      );
    }

    // Painel do jogador selecionado
    const p = rows.find(r => r.player_id === selectedPlayer);
    if (!p) return null;
    return (
      <>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => setSelectedPlayer(null)}
          style={{ padding: 0, marginBottom: 12 }}
        >
          Voltar
        </Button>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
          padding: 12, borderRadius: 12, background: '#f6ffed', border: '1px solid #b7eb8f',
        }}>
          <Avatar size={48} icon={<UserOutlined />} style={{ background: '#25D366', flexShrink: 0 }} />
          <div>
            <Text strong style={{ fontSize: 16, display: 'block' }}>{p.name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>Toque em + pra somar, − pra tirar</Text>
          </div>
        </div>

        <Row gutter={[12, 12]}>
          {FIELDS.map(f => (
            <Col xs={24} sm={12} key={f.key}>
              <div style={{
                padding: 12, borderRadius: 12,
                background: '#fff', border: '1px solid #e8e8e8',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 10, display: 'grid', placeItems: 'center',
                  fontSize: 22, background: `${f.color}22`, flexShrink: 0,
                }}>{f.emoji}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#8c8c8c', lineHeight: 1 }}>{f.label}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: p[f.key] > 0 ? f.color : '#bfbfbf', lineHeight: 1.1 }}>
                    {p[f.key] || 0}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <Button
                    shape="circle" icon={<MinusOutlined />}
                    onClick={() => bumpCell(p.player_id, f.key, -1)}
                    disabled={!p[f.key]}
                    style={{ width: 40, height: 40, minWidth: 40, flexShrink: 0, padding: 0 }}
                  />
                  <Button
                    shape="circle" type="primary" icon={<PlusOutlined />}
                    onClick={() => bumpCell(p.player_id, f.key, +1)}
                    style={{
                      width: 40, height: 40, minWidth: 40, flexShrink: 0, padding: 0,
                      background: f.color, borderColor: f.color,
                    }}
                  />
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </>
    );
  };

  const batchPanel = () => (
    <>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
        Edição em lote — tabela. Melhor pra desktop.
      </Text>
      <Table
        columns={columns}
        dataSource={rows}
        rowKey="player_id"
        pagination={false}
        size="small"
        scroll={{ x: 720, y: 400 }}
      />
    </>
  );

  const inPlayerPanel = mode === 'quick' && !!selectedPlayer;

  const footer = [
    <Button key="cancel" onClick={onClose}>Cancelar</Button>,
    inPlayerPanel ? (
      <Button
        key="save-back" type="primary" loading={saving}
        icon={<ArrowLeftOutlined />}
        onClick={saveAndBackToList}
      >
        Salvar e escolher outro
      </Button>
    ) : (
      <Button key="save-close" type="primary" loading={saving} onClick={saveAndClose}>
        Salvar tudo
      </Button>
    ),
  ];

  return (
    <Modal
      title={<span>📊 Estatísticas — <Tag>{gameLabel || `Jogo #${gameId}`}</Tag></span>}
      open={open}
      onCancel={onClose}
      footer={footer}
      width={720}
      maskClosable={false}
    >
      {loading ? <Empty description="Carregando..." /> : rows.length === 0 ? (
        <Empty description="Nenhum jogador confirmado nesse jogo." />
      ) : (
        <>
          <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'center' }}>
            <Segmented
              value={mode}
              onChange={(v) => { setMode(v); setSelectedPlayer(null); }}
              options={[
                { label: <span><ThunderboltOutlined /> Rápido</span>, value: 'quick' },
                { label: <span><TableOutlined /> Lote</span>, value: 'batch' },
              ]}
            />
          </div>
          {mode === 'quick' ? quickPanel() : batchPanel()}
        </>
      )}
    </Modal>
  );
};

export default StatsInputModal;
